/**
 * audioEngine.ts — Motor de audio baseado em Howler.js
 *
 * Substitui todos os modulos procedurais com fallback automatico:
 * - Tenta carregar arquivo de audio (OGG/MP3)
 * - Se arquivo nao existe (404), usa sistema procedural como fallback
 * - Transicao transparente: basta colocar arquivos nas pastas corretas
 *
 * API publica:
 *   engine.playMusic('menu' | 'battle' | 'story')
 *   engine.stopMusic()
 *   engine.setTension(0-1)
 *   engine.updateFromSnapshot(snapshot)
 *   engine.playSfx('shoot' | 'hit' | 'explosion' | ...)
 *   engine.playStinger('victory_match' | 'defeat' | ...)
 *   engine.setVolumes(master, music, sfx)
 *   engine.setMuted(boolean)
 */

import { Howl, Howler } from 'howler';
import {
  USE_FILE_AUDIO,
  MUSIC_TRACKS,
  STINGERS,
  SFX,
  CONTEXT_TRACKS,
  type MusicContext,
  type MusicTrackConfig,
  type SfxConfig,
  type StingerConfig,
} from './audioConfig';
import { withAudioCacheBuster } from './audioCacheBuster';

// Procedural fallbacks — importados para usar quando arquivos nao existem
import { createMenuTheme, forceStopMenuMusic } from './menuTheme';
import { AdaptiveMusicConductor, type MusicSnapshot } from './musicConductor';
import { createStoryMusic } from './storyMusic';
import {
  playSplashSound,
  playSteamSound,
  playMudSound,
  playIgniteSound,
} from './terrainSfx';
import {
  playShootSound,
  playHitSound,
  playExplosionSound,
  playSuccessSound,
  playChargedShootSound,
  warmupAudio,
} from './proceduralSfx';
import { playVictorySound, playDefeatSound } from './victorySound';

// ============================================================
// TIPOS INTERNOS
// ============================================================

interface LoadedTrack {
  howl: Howl;
  loaded: boolean;
  failed: boolean;
}

type ProceduralMusicHandle = ReturnType<typeof createMenuTheme> | ReturnType<typeof createStoryMusic>;

// ============================================================
// AUDIO ENGINE
// ============================================================

export class AudioEngine {
  // --- Howl instances ---
  private musicHowls = new Map<string, LoadedTrack>();
  private sfxHowls = new Map<string, LoadedTrack>();
  private stingerHowls = new Map<string, LoadedTrack>();

  // --- SFX rate limiting ---
  private lastSfxPlayedAt = new Map<string, number>();

  // --- Estado de musica ativa ---
  private activeMusicId: string | null = null;
  private activeContext: MusicContext = 'none';
  
  // --- Debounce e lock para evitar chamadas duplicadas ---
  private lastMusicRequestTime = 0;
  private lastMusicRequestContext: MusicContext = 'none';
  private musicRequestLock = false;
  private static readonly MUSIC_DEBOUNCE_MS = 100; // Ignora chamadas dentro de 100ms

  // --- Fade overlap prevention ---
  private fadingOutTracks = new Set<Howl>();

  // --- Voice ducking ---
  private musicDuckedForVoice = false;
  private preDuckMusicVolume = 0;
  private static readonly VOICE_DUCK_LEVEL = 0.25; // Duck music to 25% during voice
  private static readonly VOICE_DUCK_FADE_MS = 300;
  private static readonly VOICE_UNDUCK_FADE_MS = 500;

  // --- SFX ambient suppression during story context ---
  // These SFX IDs are atmospheric/ambient and should NOT play when
  // story adaptive music is already providing the atmosphere.
  // Playing them causes double audio layering.
  private static readonly STORY_AMBIENT_SFX = new Set([
    'ambient_space', 'ambient_wind', 'ambient_factory', 'ambient_garage',
    'suspense', 'story_victory',
  ]);

  // --- Fallback procedural ---
  private proceduralConductor: AdaptiveMusicConductor | null = null;
  private proceduralMenuTheme: ProceduralMusicHandle = null;
  private proceduralStoryMusic: ProceduralMusicHandle = null;
  private usingProceduralFallback = false;

  // --- Volumes ---
  private masterVolume = 0.7;
  private musicVolume = 0.5;
  private sfxVolume = 0.8;
  private _isMuted = false;

  // --- Tensao/intensidade adaptativa ---
  private currentTension = 0.25;
  private crossfadeInterval: ReturnType<typeof setInterval> | null = null;

  // --- Estado ---
  private initialized = false;
  private loadingCheckInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    if (USE_FILE_AUDIO) {
      this.preloadAll();
    }
    warmupAudio();
    this.initialized = true;
  }

  // ============================================================
  // PRELOAD
  // ============================================================

  private preloadAll(): void {
    // Musica: preload 'auto' tracks
    for (const cfg of Object.values(MUSIC_TRACKS)) {
      if (cfg.preload === 'auto') {
        this.getOrCreateMusic(cfg);
      }
    }
    // SFX: preload todos (sao pequenos)
    for (const cfg of Object.values(SFX)) {
      this.getOrCreateSfx(cfg);
    }
    // Stingers: preload todos
    for (const cfg of Object.values(STINGERS)) {
      this.getOrCreateStinger(cfg);
    }
  }

  private getOrCreateMusic(cfg: MusicTrackConfig): LoadedTrack {
    if (this.musicHowls.has(cfg.id)) {
      return this.musicHowls.get(cfg.id)!;
    }

    const track: LoadedTrack = {
      howl: new Howl({
        src: withAudioCacheBuster(cfg.src),
        loop: cfg.loop,
        volume: 0,
        preload: true,
        html5: false, // Use Web Audio API for better compatibility
        onload: () => {
          track.loaded = true;
          console.log(`[AudioEngine] Music loaded: ${cfg.id}`);
        },
        onloaderror: (_id: number, error: unknown) => {
          track.failed = true;
          console.log(`[AudioEngine] Music not found: ${cfg.id} (procedural fallback)`, error);
        },
      }),
      loaded: false,
      failed: false,
    };

    this.musicHowls.set(cfg.id, track);
    return track;
  }

  private getOrCreateSfx(cfg: SfxConfig): LoadedTrack {
    if (this.sfxHowls.has(cfg.id)) {
      return this.sfxHowls.get(cfg.id)!;
    }

    const track: LoadedTrack = {
      howl: new Howl({
        src: withAudioCacheBuster(cfg.src),
        volume: cfg.volume,
        preload: true,
        pool: cfg.pool,
        onload: () => { track.loaded = true; },
        onloaderror: () => { track.failed = true; },
      }),
      loaded: false,
      failed: false,
    };

    this.sfxHowls.set(cfg.id, track);
    return track;
  }

  private getOrCreateStinger(cfg: StingerConfig): LoadedTrack {
    if (this.stingerHowls.has(cfg.id)) {
      return this.stingerHowls.get(cfg.id)!;
    }

    const track: LoadedTrack = {
      howl: new Howl({
        src: withAudioCacheBuster(cfg.src),
        volume: cfg.volume,
        preload: true,
        onload: () => { track.loaded = true; },
        onloaderror: () => { track.failed = true; },
      }),
      loaded: false,
      failed: false,
    };

    this.stingerHowls.set(cfg.id, track);
    return track;
  }

  // ============================================================
  // VOLUME CONTROL
  // ============================================================

  setVolumes(master: number, music: number, sfx: number): void {
    this.masterVolume = master;
    this.musicVolume = music;
    this.sfxVolume = sfx;

    // Propagar para fallback procedural se ativo
    if (this.usingProceduralFallback) {
      this.proceduralConductor?.setVolumes(master, music);
      this.proceduralConductor?.setMuted(this._isMuted);
    }

    // Atualizar volume da musica ativa (file-based)
    this.applyMusicVolume();
  }

  setMuted(muted: boolean): void {
    this._isMuted = muted;
    Howler.mute(muted);

    if (this.usingProceduralFallback) {
      this.proceduralConductor?.setMuted(muted);
    }
  }

  get isMuted(): boolean {
    return this._isMuted;
  }

  private effectiveMusicVolume(): number {
    return this._isMuted ? 0 : this.masterVolume * this.musicVolume;
  }

  private effectiveSfxVolume(baseVol: number): number {
    return this._isMuted ? 0 : this.masterVolume * this.sfxVolume * baseVol;
  }

  private applyMusicVolume(): void {
    if (!this.activeMusicId || this.usingProceduralFallback) return;

    const track = this.musicHowls.get(this.activeMusicId);
    const cfg = MUSIC_TRACKS[this.activeMusicId];
    if (track?.loaded && cfg) {
      track.howl.volume(this.effectiveMusicVolume() * cfg.volume);
    }
  }

  // ============================================================
  // MUSICA — API PUBLICA
  // ============================================================

  /** Inicia musica para um contexto. Gerencia crossfade e fallback.
   *  Inclui debounce para evitar chamadas duplicadas de múltiplos MusicAdapters.
   */
  playMusic(context: MusicContext): void {
    const now = Date.now();

    // Force-stop any fading-out tracks to prevent overlap
    if (this.fadingOutTracks.size > 0) {
      for (const howl of this.fadingOutTracks) {
        howl.stop();
      }
      this.fadingOutTracks.clear();
    }
    
    // Debounce: ignora chamadas muito próximas para o mesmo contexto
    if (
      context === this.lastMusicRequestContext &&
      now - this.lastMusicRequestTime < AudioEngine.MUSIC_DEBOUNCE_MS
    ) {
      return;
    }
    
    // Lock: se já tem uma operação em andamento, ignora
    if (this.musicRequestLock) {
      return;
    }
    
    // Atualiza tracking
    this.lastMusicRequestTime = now;
    this.lastMusicRequestContext = context;
    
    // Se já está tocando o mesmo contexto, não faz nada
    if (this.activeContext === context) return;

    // Adquire lock
    this.musicRequestLock = true;

    // Parar musica anterior
    this.stopMusic();
    this.activeContext = context;

    // Libera lock após operação
    setTimeout(() => {
      this.musicRequestLock = false;
    }, 50);

    if (context === 'none') return;

    if (!USE_FILE_AUDIO) {
      this.startProceduralMusic(context);
      return;
    }

    // Tentar file-based primeiro
    const trackIds = CONTEXT_TRACKS[context];
    if (!trackIds?.length) return;

    // Garantir que todos os tracks do contexto foram criados (lazy load para preload: 'none')
    for (const id of trackIds) {
      const cfg = MUSIC_TRACKS[id];
      if (cfg) {
        this.getOrCreateMusic(cfg);
      }
    }

    // Checar se pelo menos um track do contexto carregou com sucesso
    const hasLoadedTrack = trackIds.some(id => {
      const t = this.musicHowls.get(id);
      return t && t.loaded && !t.failed;
    });

    if (hasLoadedTrack) {
      this.startFileMusic(context);
    } else {
      // Tracks ainda carregando — poll ate carregar ou falhar
      this.waitForTracksAndPlay(context, trackIds);
    }
  }

  /** Aguarda tracks carregarem com polling, fallback procedural se todos falharem */
  private waitForTracksAndPlay(context: MusicContext, trackIds: string[]): void {
    // Limpar polling anterior se existir
    if (this.loadingCheckInterval) {
      clearInterval(this.loadingCheckInterval);
      this.loadingCheckInterval = null;
    }

    let attempts = 0;
    const maxAttempts = 30; // 30 * 500ms = 15s max

    this.loadingCheckInterval = setInterval(() => {
      attempts++;

      // Contexto mudou, parar polling
      if (this.activeContext !== context) {
        clearInterval(this.loadingCheckInterval!);
        this.loadingCheckInterval = null;
        return;
      }

      const hasLoaded = trackIds.some(id => {
        const t = this.musicHowls.get(id);
        return t && t.loaded && !t.failed;
      });

      if (hasLoaded) {
        clearInterval(this.loadingCheckInterval!);
        this.loadingCheckInterval = null;
        this.startFileMusic(context);
        return;
      }

      // Todos falharam ou timeout
      const allFailed = trackIds.every(id => {
        const t = this.musicHowls.get(id);
        return t && t.failed;
      });

      if (allFailed || attempts >= maxAttempts) {
        clearInterval(this.loadingCheckInterval!);
        this.loadingCheckInterval = null;
        if (this.activeContext === context) {
          this.startProceduralMusic(context);
        }
      }
    }, 500);
  }

  stopMusic(): void {
    // Liberar lock de request
    this.musicRequestLock = false;
    
    // Parar loading polling
    if (this.loadingCheckInterval) {
      clearInterval(this.loadingCheckInterval);
      this.loadingCheckInterval = null;
    }

    // Parar crossfade polling
    if (this.crossfadeInterval) {
      clearInterval(this.crossfadeInterval);
      this.crossfadeInterval = null;
    }

    // Parar file-based
    if (this.activeMusicId && !this.usingProceduralFallback) {
      const track = this.musicHowls.get(this.activeMusicId);
      const cfg = MUSIC_TRACKS[this.activeMusicId];
      if (track?.loaded && cfg) {
        const howl = track.howl;
        track.howl.fade(track.howl.volume(), 0, cfg.fadeOut);
        // Track fading Howl to prevent overlap if playMusic() is called during fade
        this.fadingOutTracks.add(howl);
        setTimeout(() => {
          howl.stop();
          this.fadingOutTracks.delete(howl);
        }, cfg.fadeOut);
      }
    }

    // Parar procedural
    if (this.usingProceduralFallback) {
      this.stopProceduralMusic();
    }

    this.activeMusicId = null;
    this.activeContext = 'none';
    this.usingProceduralFallback = false;
    this.musicDuckedForVoice = false;
  }

  // ============================================================
  // MUSICA — FILE-BASED
  // ============================================================

  private startFileMusic(context: MusicContext): void {
    this.usingProceduralFallback = false;

    if (context === 'menu') {
      this.startFileTrack('menu_main');
    } else if (context === 'credits') {
      this.startFileTrack('credits_main');
    } else if (context === 'battle' || context === 'story') {
      this.startAdaptiveFileMusic(context === 'battle' ? 'battle' : 'story');
    } else if (context === 'battle_royale') {
      this.startFileTrack('battle_royale');
    } else if (context === 'battle_genai') {
      this.startFileTrack('battle_genai');
    } else if (context === 'tutorial') {
      this.startFileTrack('tutorial');
    } else if (context === 'game_over') {
      this.startFileTrack('game_over');
    }
  }

  private startFileTrack(trackId: string): void {
    const cfg = MUSIC_TRACKS[trackId];
    if (!cfg) return;

    const track = this.musicHowls.get(trackId);
    if (!track?.loaded || track.failed) return;

    const targetVol = this.effectiveMusicVolume() * cfg.volume;
    track.howl.volume(0);
    track.howl.play();
    track.howl.fade(0, targetVol, cfg.fadeIn);

    this.activeMusicId = trackId;
  }

  private startAdaptiveFileMusic(prefix: 'battle' | 'story'): void {
    const trackId = this.pickTrackForTension(prefix, this.currentTension);
    if (trackId) {
      this.startFileTrack(trackId);
    }

    // Poll para crossfade adaptativo
    this.crossfadeInterval = setInterval(() => {
      if (!this.usingProceduralFallback) {
        this.evaluateAdaptiveCrossfade(prefix);
      }
    }, 500);
  }

  private pickTrackForTension(prefix: string, tension: number): string | null {
    const candidates = Object.values(MUSIC_TRACKS).filter(
      t => t.id.startsWith(prefix) && t.intensityRange
    );
    for (const cfg of candidates) {
      const [min, max] = cfg.intensityRange!;
      if (tension >= min && tension <= max) {
        // Verificar se o track carregou
        const track = this.musicHowls.get(cfg.id);
        if (track?.loaded && !track.failed) {
          return cfg.id;
        }
      }
    }
    // Fallback: primeiro track carregado do prefixo
    for (const cfg of candidates) {
      const track = this.musicHowls.get(cfg.id);
      if (track?.loaded && !track.failed) return cfg.id;
    }
    return null;
  }

  private evaluateAdaptiveCrossfade(prefix: string): void {
    const idealTrack = this.pickTrackForTension(prefix, this.currentTension);
    if (!idealTrack || idealTrack === this.activeMusicId) return;
    this.crossfadeTo(idealTrack);
  }

  private crossfadeTo(newTrackId: string): void {
    const oldId = this.activeMusicId;
    const newCfg = MUSIC_TRACKS[newTrackId];
    if (!newCfg) return;

    const newTrack = this.musicHowls.get(newTrackId);
    if (!newTrack?.loaded || newTrack.failed) return;

    const targetVol = this.effectiveMusicVolume() * newCfg.volume;

    // Fade out anterior
    if (oldId) {
      const oldTrack = this.musicHowls.get(oldId);
      const oldCfg = MUSIC_TRACKS[oldId];
      if (oldTrack?.loaded && oldCfg) {
        oldTrack.howl.fade(oldTrack.howl.volume(), 0, oldCfg.fadeOut);
        setTimeout(() => oldTrack.howl.stop(), oldCfg.fadeOut);
      }
    }

    // Fade in novo
    newTrack.howl.volume(0);
    newTrack.howl.play();
    newTrack.howl.fade(0, targetVol, newCfg.fadeIn);

    this.activeMusicId = newTrackId;
  }

  // ============================================================
  // MUSICA — FALLBACK PROCEDURAL
  // ============================================================

  private startProceduralMusic(context: MusicContext): void {
    this.usingProceduralFallback = true;
    console.log(`[AudioEngine] Using procedural fallback for context: ${context}`);

    if (context === 'menu') {
      this.proceduralMenuTheme = createMenuTheme();
      if (this.proceduralMenuTheme) {
        this.proceduralMenuTheme.play();
        const vol = this.effectiveMusicVolume() * 0.6;
        this.proceduralMenuTheme.volume(vol);
      }
    } else if (context === 'battle') {
      if (!this.proceduralConductor) {
        this.proceduralConductor = new AdaptiveMusicConductor('industrial');
      }
      this.proceduralConductor.setVolumes(this.masterVolume, this.musicVolume);
      this.proceduralConductor.setMuted(this._isMuted);
      this.proceduralConductor.start();
    } else if (context === 'story') {
      this.proceduralStoryMusic = createStoryMusic();
      if (this.proceduralStoryMusic) {
        this.proceduralStoryMusic.play();
        const vol = this.effectiveMusicVolume() * 0.6;
        this.proceduralStoryMusic.volume(vol);
      }
    }
  }

  private stopProceduralMusic(): void {
    forceStopMenuMusic();
    this.proceduralMenuTheme?.stop();
    this.proceduralMenuTheme = null;

    this.proceduralConductor?.stop();

    this.proceduralStoryMusic?.stop();
    this.proceduralStoryMusic = null;
  }

  // ============================================================
  // TENSAO / INTENSIDADE — API PUBLICA
  // ============================================================

  /** Define tensao diretamente (0.0 a 1.0) */
  setTension(tension: number): void {
    this.currentTension = Math.max(0, Math.min(1, tension));

    // Propagar para story procedural se ativo
    if (this.usingProceduralFallback && this.activeContext === 'story' && this.proceduralStoryMusic) {
      (this.proceduralStoryMusic as any).setIntensity?.(this.currentTension);
    }
  }

  /** Calcula tensao a partir de snapshot de jogo (mesma formula do musicConductor) */
  updateFromSnapshot(snapshot: MusicSnapshot): void {
    const density = snapshot.totalEnemies > 0
      ? snapshot.aliveEnemies / snapshot.totalEnemies : 0;
    const healthPressure = 1 - Math.max(0, Math.min(1, snapshot.playerHealthPct));
    const boss = snapshot.hasBoss ? 0.2 : 0;
    const coop = snapshot.isMultiplayer ? 0.1 : 0;
    const wavePressure = Math.max(0, Math.min(1, snapshot.wave / 10)) * 0.15;

    const raw = 0.3 + density * 0.35 + healthPressure * 0.3 + boss + coop + wavePressure;
    this.currentTension = Math.max(0, Math.min(1, raw));

    // Propagar para fallback procedural
    if (this.usingProceduralFallback && this.proceduralConductor) {
      this.proceduralConductor.setVolumes(this.masterVolume, this.musicVolume);
      this.proceduralConductor.setMuted(this._isMuted);
      this.proceduralConductor.setTerrain(snapshot.terrain);
      this.proceduralConductor.update(snapshot);
    }
  }

  // ============================================================
  // SFX — API PUBLICA
  // ============================================================

  playSfx(sfxId: string, volumeOverride?: number): void {
    if (this._isMuted) return;

    const cfg = SFX[sfxId];
    if (!cfg) return;

    // Skip ambient/atmospheric SFX when story music is active.
    // The adaptive music system (calm↔tense via setTension) already provides atmosphere.
    // Playing these SFX would layer duplicate music tracks on top of the active music.
    if (this.activeContext === 'story' && AudioEngine.STORY_AMBIENT_SFX.has(sfxId)) {
      return;
    }

    if (cfg.minIntervalMs) {
      const now = Date.now();
      const last = this.lastSfxPlayedAt.get(sfxId) ?? 0;
      if (now - last < cfg.minIntervalMs) return;
      this.lastSfxPlayedAt.set(sfxId, now);
    }

    // Tentar file-based
    if (USE_FILE_AUDIO) {
      const track = this.sfxHowls.get(sfxId);
      if (track?.loaded && !track.failed) {
        const vol = this.effectiveSfxVolume(volumeOverride ?? cfg.volume);
        const id = track.howl.play();
        track.howl.volume(vol, id);

        if (cfg.rateRange) {
          const [min, max] = cfg.rateRange;
          track.howl.rate(min + Math.random() * (max - min), id);
        }
        return;
      }
    }

    // Fallback procedural.
    //
    // `volumeOverride` é volume **relativo** (0..1), a mesma semântica do
    // caminho por arquivo — que já o passava por `effectiveSfxVolume`. Aqui ele
    // era usado como valor absoluto, então um som com override ignorava os
    // controles de volume mestre e de efeitos: quem tinha baixado o volume no
    // menu continuava ouvindo o tiro carregado no volume cheio.
    const vol = this.effectiveSfxVolume(volumeOverride ?? cfg.volume);
    switch (sfxId) {
      case 'shoot': playShootSound(vol); break;
      case 'hit': playHitSound(vol); break;
      case 'explosion': playExplosionSound(vol); break;
      case 'success': playSuccessSound(vol); break;
      case 'charged_shoot': playChargedShootSound(0.5, vol); break;
      case 'terrain_splash': playSplashSound(vol); break;
      case 'terrain_steam': playSteamSound(vol); break;
      case 'terrain_mud': playMudSound(vol); break;
      case 'terrain_ignite': playIgniteSound(vol); break;
      default:
        // Story SFX — importar dinamicamente quando necessario
        this.playProceduralStorySfx(sfxId, vol);
    }
  }

  /**
   * Toca um SFX com o volume do catálogo multiplicado por `scale`.
   *
   * É o que permite dizer "isto aconteceu com outro tanque, toca mais baixo"
   * sem que quem chama precise conhecer o volume-base daquele som.
   */
  playSfxScaled(sfxId: string, scale: number): void {
    const cfg = SFX[sfxId];
    if (!cfg || scale <= 0) return;
    this.playSfx(sfxId, cfg.volume * Math.min(1, scale));
  }

  private async playProceduralStorySfx(sfxId: string, volume: number): Promise<void> {
    try {
      const storySfx = await import('./storySfx');
      const fnMap: Record<string, (v: number) => void> = {
        ambient_space: storySfx.playAmbientSpace,
        ambient_wind: storySfx.playAmbientWind,
        ambient_factory: storySfx.playAmbientFactory,
        ambient_garage: storySfx.playAmbientGarage,
        suspense: storySfx.playSuspense,
        power_up: storySfx.playPowerUp,
        boot: storySfx.playBoot,
        dialogue: storySfx.playDialogue,
        alert: storySfx.playAlert,
        story_victory: storySfx.playVictory,
        victory: storySfx.playVictory,
        // credits_music: if no file-based asset is available, fallback to a long ambient
        credits_music: storySfx.playAmbientSpace,
        // Dungeon-specific procedural SFX
        door_unlock: storySfx.playDoorUnlock,
        door_open: storySfx.playDoorOpen,
        room_enter: storySfx.playRoomEnter,
        room_clear_sfx: storySfx.playRoomClear,
        energy_absorb: storySfx.playEnergyAbsorb,
        enemy_spawn: storySfx.playEnemySpawn,
        wave_clear: storySfx.playWaveClear,
        dungeon_intro: storySfx.playDungeonIntro,
        transition_whoosh: storySfx.playTransitionWhoosh,
        boss_intro: storySfx.playBossIntro,
      };
      fnMap[sfxId]?.(volume);
    } catch {
      // storySfx nao disponivel
    }
  }

  // ============================================================
  // STINGERS — API PUBLICA
  // ============================================================

  playStinger(stingerId: string): void {
    if (this._isMuted) return;

    const cfg = STINGERS[stingerId];
    if (!cfg) return;

    // Tentar file-based
    if (USE_FILE_AUDIO) {
      const track = this.stingerHowls.get(stingerId);
      if (track?.loaded && !track.failed) {
        const vol = this.effectiveSfxVolume(cfg.volume);

        // Duck music se configurado
        if (cfg.duckMusic && this.activeMusicId && !this.usingProceduralFallback) {
          const musicTrack = this.musicHowls.get(this.activeMusicId);
          if (musicTrack?.loaded) {
            const currentVol = musicTrack.howl.volume();
            const duckVol = currentVol * cfg.duckLevel;
            musicTrack.howl.fade(currentVol, duckVol, 200);

            track.howl.once('end', () => {
              musicTrack.howl.fade(duckVol, currentVol, 500);
            });
          }
        }

        track.howl.volume(vol);
        track.howl.play();
        return;
      }
    }

    // Fallback procedural
    const vol = this.masterVolume * this.sfxVolume * cfg.volume;
    switch (stingerId) {
      case 'victory_wave':
      case 'victory_match':
        playVictorySound(vol);
        break;
      case 'defeat':
        playDefeatSound(vol);
        break;
      case 'room_clear':
        // Use procedural room clear fanfare
        this.playProceduralStorySfx('room_clear_sfx', vol);
        break;
      case 'boss_intro':
        // Use procedural boss intro
        this.playProceduralStorySfx('boss_intro', vol);
        break;
      // boss_warning nao tem fallback procedural direto
    }
  }

  // ============================================================
  // VOICE DUCKING — Abaixa música quando narração de voz toca
  // ============================================================

  /** Duck music volume when voice narration starts */
  duckMusicForVoice(): void {
    if (this.musicDuckedForVoice) return;
    if (!this.activeMusicId || this.usingProceduralFallback) return;

    const track = this.musicHowls.get(this.activeMusicId);
    if (!track?.loaded) return;

    this.musicDuckedForVoice = true;
    this.preDuckMusicVolume = track.howl.volume();
    const duckVol = this.preDuckMusicVolume * AudioEngine.VOICE_DUCK_LEVEL;
    track.howl.fade(this.preDuckMusicVolume, duckVol, AudioEngine.VOICE_DUCK_FADE_MS);
  }

  /** Restore music volume when voice narration ends */
  unduckMusicForVoice(): void {
    if (!this.musicDuckedForVoice) return;
    this.musicDuckedForVoice = false;

    if (!this.activeMusicId || this.usingProceduralFallback) return;

    const track = this.musicHowls.get(this.activeMusicId);
    if (!track?.loaded) return;

    const currentVol = track.howl.volume();
    const targetVol = this.preDuckMusicVolume > 0
      ? this.preDuckMusicVolume
      : this.effectiveMusicVolume() * (MUSIC_TRACKS[this.activeMusicId]?.volume ?? 0.5);
    track.howl.fade(currentVol, targetVol, AudioEngine.VOICE_UNDUCK_FADE_MS);
  }

  /** Check if music is currently ducked for voice */
  get isDuckedForVoice(): boolean {
    return this.musicDuckedForVoice;
  }

  // ============================================================
  // LIFECYCLE
  // ============================================================

  /** Forca resume do AudioContext (chamar apos interacao do usuario) */
  forceResume(): void {
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      Howler.ctx.resume().catch(() => {});
    }
    this.proceduralConductor?.forceResume();
  }

  destroy(): void {
    this.stopMusic();
    // Force-stop any fading-out tracks
    for (const howl of this.fadingOutTracks) {
      howl.stop();
    }
    this.fadingOutTracks.clear();
    this.musicDuckedForVoice = false;
    this.musicHowls.forEach(t => t.howl.unload());
    this.sfxHowls.forEach(t => t.howl.unload());
    this.stingerHowls.forEach(t => t.howl.unload());
    this.musicHowls.clear();
    this.sfxHowls.clear();
    this.stingerHowls.clear();
  }
}

// ============================================================
// SINGLETON
// ============================================================

let engineInstance: AudioEngine | null = null;

export function getAudioEngine(): AudioEngine {
  if (!engineInstance) {
    engineInstance = new AudioEngine();
  }
  return engineInstance;
}

export function resetAudioEngine(): void {
  if (engineInstance) {
    engineInstance.destroy();
    engineInstance = null;
  }
}
